import React from 'react';
import { connect } from 'react-redux';
import useTheme from '../Hooks/useTheme';

/**
 * Theme Demo Component
 * Displays all theme colors and elements for visual testing
 */
const ThemeDemo = ({ themeSettings }) => {
    const theme = useTheme();
    
    const styles = {
        container: {
            padding: '40px',
            fontFamily: 'var(--font-family)',
            backgroundColor: 'var(--color-background)',
            minHeight: '100vh'
        },
        title: {
            fontSize: '32px',
            fontWeight: 'bold',
            marginBottom: '32px',
            color: 'var(--color-text)'
        },
        section: {
            marginBottom: '40px'
        },
        sectionTitle: {
            fontSize: '20px',
            fontWeight: '600',
            marginBottom: '16px',
            color: 'var(--color-text)',
            borderBottom: '2px solid var(--color-primary)',
            paddingBottom: '8px',
            display: 'inline-block'
        },
        colorGrid: {
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
            gap: '16px'
        },
        colorBox: {
            padding: '16px',
            borderRadius: 'var(--spacing-border-radius)',
            textAlign: 'center',
            boxShadow: 'var(--effect-box-shadow-sm)',
            border: '1px solid var(--color-border)'
        },
        colorSwatch: {
            width: '60px',
            height: '60px',
            borderRadius: '8px',
            margin: '0 auto 8px',
            border: '1px solid var(--color-border)'
        },
        colorName: {
            fontSize: '12px',
            fontWeight: '500',
            color: 'var(--color-text)'
        },
        colorValue: {
            fontSize: '11px',
            color: 'var(--color-text-muted)',
            marginTop: '4px'
        },
        buttonRow: {
            display: 'flex',
            gap: '16px',
            flexWrap: 'wrap',
            marginBottom: '24px'
        },
        primaryBtn: {
            padding: '12px 24px',
            backgroundColor: 'var(--color-button-primary-bg)',
            color: 'var(--color-button-primary-text)',
            border: 'none',
            borderRadius: 'var(--spacing-border-radius)',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500',
            transition: 'var(--effect-transition)'
        },
        secondaryBtn: {
            padding: '12px 24px',
            backgroundColor: 'var(--color-button-secondary-bg)',
            color: 'var(--color-button-secondary-text)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--spacing-border-radius)',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500',
            transition: 'var(--effect-transition)'
        },
        outlineBtn: {
            padding: '12px 24px',
            backgroundColor: 'transparent',
            color: 'var(--color-primary)',
            border: '2px solid var(--color-primary)',
            borderRadius: 'var(--spacing-border-radius)',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500',
            transition: 'var(--effect-transition)'
        },
        card: {
            backgroundColor: 'var(--color-card-background)',
            border: '1px solid var(--color-card-border)',
            borderRadius: 'var(--spacing-border-radius)',
            padding: '24px',
            boxShadow: 'var(--effect-box-shadow-card)',
            maxWidth: '400px'
        },
        cardTitle: {
            fontSize: '18px',
            fontWeight: '600',
            color: 'var(--color-text)',
            marginBottom: '8px'
        },
        cardText: {
            fontSize: '14px',
            color: 'var(--color-text-muted)',
            marginBottom: '16px'
        },
        input: {
            width: '100%',
            padding: '12px 16px',
            backgroundColor: 'var(--color-input-background)',
            border: '1px solid var(--color-input-border)',
            borderRadius: 'var(--spacing-border-radius-sm)',
            fontSize: '14px',
            marginBottom: '16px',
            boxSizing: 'border-box'
        },
        statusRow: {
            display: 'flex',
            gap: '16px',
            flexWrap: 'wrap'
        },
        statusBadge: {
            padding: '6px 12px',
            borderRadius: '20px',
            fontSize: '12px',
            fontWeight: '500'
        },
        link: {
            color: 'var(--color-link-color)',
            textDecoration: 'none',
            cursor: 'pointer'
        },
        themeStatus: {
            padding: '16px',
            backgroundColor: 'var(--color-primary-light, #E6F3FF)',
            borderRadius: 'var(--spacing-border-radius)',
            marginBottom: '32px',
            border: '1px solid var(--color-primary)'
        }
    };

    const colorPairs = [
        { name: 'Primary', key: 'primary', value: theme.colors?.primary },
        { name: 'Primary Hover', key: 'primaryHover', value: theme.colors?.primaryHover },
        { name: 'Primary Light', key: 'primaryLight', value: theme.colors?.primaryLight },
        { name: 'Secondary', key: 'secondary', value: theme.colors?.secondary },
        { name: 'Background', key: 'background', value: theme.colors?.background },
        { name: 'Text', key: 'text', value: theme.colors?.text },
        { name: 'Text Muted', key: 'textMuted', value: theme.colors?.textMuted },
        { name: 'Success', key: 'success', value: theme.colors?.success },
        { name: 'Error', key: 'error', value: theme.colors?.error },
        { name: 'Warning', key: 'warning', value: theme.colors?.warning },
        { name: 'Info', key: 'info', value: theme.colors?.info },
        { name: 'Border', key: 'border', value: theme.colors?.border }
    ];

    return (
        <div style={styles.container}>
            <h1 style={styles.title}>🎨 Theme Demo</h1>
            
            {/* Theme Status */}
            <div style={styles.themeStatus}>
                <strong>Theme Status:</strong> {themeSettings ? '✅ Custom theme loaded from API' : '⚪ Using default theme'}
                {themeSettings && (
                    <div style={{ marginTop: '8px', fontSize: '13px' }}>
                        Primary: <strong>{themeSettings.colors?.primary}</strong> | 
                        Secondary: <strong>{themeSettings.colors?.secondary}</strong>
                    </div>
                )}
            </div>

            {/* Colors Section */}
            <div style={styles.section}>
                <h2 style={styles.sectionTitle}>Colors</h2>
                <div style={styles.colorGrid}>
                    {colorPairs.map(({ name, key, value }) => (
                        <div key={key} style={styles.colorBox}>
                            <div style={{ ...styles.colorSwatch, backgroundColor: value || '#ccc' }} />
                            <div style={styles.colorName}>{name}</div>
                            <div style={styles.colorValue}>{value || 'N/A'}</div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Buttons Section */}
            <div style={styles.section}>
                <h2 style={styles.sectionTitle}>Buttons</h2>
                <div style={styles.buttonRow}>
                    <button style={styles.primaryBtn}>Primary Button</button>
                    <button style={styles.secondaryBtn}>Secondary Button</button>
                    <button style={styles.outlineBtn}>Outline Button</button>
                </div>
                <div style={styles.buttonRow}>
                    <button className="btn-theme-primary" style={{ padding: '12px 24px', borderRadius: '8px', border: 'none', cursor: 'pointer' }}>
                        Using .btn-theme-primary
                    </button>
                    <button className="btn-theme-outline" style={{ padding: '12px 24px', borderRadius: '8px', cursor: 'pointer' }}>
                        Using .btn-theme-outline
                    </button>
                </div>
            </div>

            {/* Card Section */}
            <div style={styles.section}>
                <h2 style={styles.sectionTitle}>Card</h2>
                <div style={styles.card}>
                    <h3 style={styles.cardTitle}>Sample Card Title</h3>
                    <p style={styles.cardText}>
                        This is a sample card using theme colors. The background, border, and shadow 
                        are all controlled by CSS variables.
                    </p>
                    <input 
                        style={styles.input} 
                        type="text" 
                        placeholder="Themed input field..." 
                    />
                    <button style={styles.primaryBtn}>Book Now</button>
                </div>
            </div>

            {/* Status Badges */}
            <div style={styles.section}>
                <h2 style={styles.sectionTitle}>Status Colors</h2>
                <div style={styles.statusRow}>
                    <span style={{ 
                        ...styles.statusBadge, 
                        backgroundColor: 'var(--color-success-light)', 
                        color: 'var(--color-success)' 
                    }}>
                        ✓ Success
                    </span>
                    <span style={{ 
                        ...styles.statusBadge, 
                        backgroundColor: 'var(--color-error-light)', 
                        color: 'var(--color-error)' 
                    }}>
                        ✕ Error
                    </span>
                    <span style={{ 
                        ...styles.statusBadge, 
                        backgroundColor: 'var(--color-warning-light)', 
                        color: 'var(--color-warning)' 
                    }}>
                        ⚠ Warning
                    </span>
                    <span style={{ 
                        ...styles.statusBadge, 
                        backgroundColor: 'var(--color-info-light)', 
                        color: 'var(--color-info)' 
                    }}>
                        ℹ Info
                    </span>
                </div>
            </div>

            {/* Links */}
            <div style={styles.section}>
                <h2 style={styles.sectionTitle}>Links</h2>
                <p>
                    This is a <a href="#" style={styles.link}>themed link</a> that uses the primary color.
                    You can also use the <span className="link-theme" style={{ cursor: 'pointer' }}>.link-theme class</span>.
                </p>
            </div>

            {/* Typography */}
            <div style={styles.section}>
                <h2 style={styles.sectionTitle}>Typography</h2>
                <h1 style={{ color: 'var(--color-text)', fontSize: 'var(--font-size-h1)' }}>Heading 1</h1>
                <h2 style={{ color: 'var(--color-text)', fontSize: 'var(--font-size-h2)' }}>Heading 2</h2>
                <h3 style={{ color: 'var(--color-text)', fontSize: 'var(--font-size-h3)' }}>Heading 3</h3>
                <p style={{ color: 'var(--color-text)', fontSize: 'var(--font-size-base)' }}>
                    Regular paragraph text with base font size.
                </p>
                <p style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)' }}>
                    Muted small text for secondary information.
                </p>
            </div>
        </div>
    );
};

const mapStateToProps = (globalState) => ({
    themeSettings: globalState.mainReducerData.themeSettings
});

export default connect(mapStateToProps)(ThemeDemo);
